// useUploadsList — fetch completed uploads for one project.
//
// Empty list is a valid response; the hook returns ``data = []``
// when the project has no completed uploads. Never throws on the
// happy-path "fresh project, no uploads yet" case.
import { useQuery } from "react-query";
import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";

// Live progress of a multi-segment OCR pre-warm (a large scanned PDF being
// transcribed across worker segments). Present only while
// ``extraction_status === "running"``; null/absent otherwise. ``done``/``total``
// are page counts (a determinate bar); ``label`` is the operator-facing line the
// BE writes ("transcribed N of M pages…" / a hand-off message).
export interface ExtractionProgress {
  done: number;
  total: number;
  label: string;
  segment: number;
  updated_at: string;
}

// One place the file's bytes were seen (content identity layer 1, design 3.7).
// A unique file has one row on the Files tab with N sightings underneath.
export interface Sighting {
  source:
    | "drive_sync"
    | "drive_pick"
    | "gmail_attachment"
    | "gmail_zip_member"
    | "gmail_thread"
    | "manual"
    | "bma_sync"
    | string;
  // The upload's canonical ingestion origin (metadata_blob.source), joined on by
  // the BE.
  origin?: string | null;
  // How this sighting is described, resolved by the BE (upload_origins): a 'manual'
  // sighting is unreliable, so its channel comes from `origin`; `source` is only used
  // for detail.
  channel: string;
  display_name: string;
  // True when this sighting flags the content deprecated: a Drive path or
  // filename containing "deprecated" (design 3.6).
  deprecated: boolean;
  drive_path?: string | null;
  drive_modified_time?: string | null;
  thread_subject?: string | null;
  sender?: string | null;
  // The carrying message's recipients for an attachment sighting; the union
  // across every message for a thread sighting.
  recipients?: { to?: string[]; cc?: string[] } | null;
  // For an attachment, its message's Date header. For a gmail_thread sighting,
  // the thread's LAST message time (the instant the list orders threads by).
  received_at?: string | null;
  // gmail_thread shape (null for every other source) - the thread's structural
  // metadata travels with its sighting rather than being an extracted fact.
  thread_first_message_at?: string | null;
  thread_message_count?: number | null;
  thread_attachments?: { count?: number; names?: string[] } | null;
  source_zip_filename?: string | null;
  // The ARCHIVE's Drive file id, on a ``drive_sync_zip_member`` sighting. Drive
  // has no address for a file INSIDE a zip, so a member's only meaningful link
  // target is the archive it came from.
  source_zip_drive_file_id?: string | null;
  uploaded_by?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
}

export interface PossibleOtherSite {
  other_site: string | null;
  confidence: string;
  rationale: string | null;
  also_covers_this_site: boolean;
}

export type FilesSection =
  | "drive"
  | "email_thread"
  | "email_attachment"
  | "withheld_attachment"
  | "direct"
  | "unaccounted"
  | "quarantined"
  | "wrong_site_excluded"
  | "hidden";

export interface UploadRecord {
  upload_id: string;
  project_id: string;
  original_name: string;
  content_type: string;
  size_bytes: number | null;
  s3_uri: string;
  initiated_at: string;
  completed_at: string;
  etag: string | null;
  // ``source`` records where the file was imported from. Defaults
  // server-side to "local" for any row pre-dating the source
  // tracking feature, so legacy uploads render as Local in the UI.
  source:
    | "local"
    | "drive"
    | "gmail"
    | "calculated"
    | "agent_generated"
    | string;
  drive_file_id: string | null;
  // Gmail-sourced fields. ``gmail_thread_id`` is the canonical
  // thread the file was pulled from (drives the Refresh button on
  // gmail rows, mirroring drive_file_id for Drive rows).
  // ``pulled_by`` is the Kinde user id of the operator who imported
  // it; ``pulled_by_name`` their display name; ``pulled_from`` the
  // mailbox address. Refresh is gated to the original importer.
  gmail_thread_id?: string;
  // The Files-page section this row renders in, decided by the BE classifier that
  // ``/uploads/tree`` also reads (files_sections.py). Null only for a row that landed
  // between the list read and the classification, which the next fetch places.
  files_section: FilesSection | null;
  // A mail attachment whose text is served with its credentials removed: its file
  // still holds them, so it cannot be opened.
  credentials_redacted?: boolean;
  pulled_by?: string;
  pulled_by_name?: string;
  pulled_from?: string;
  last_refreshed_at: string | null;
  // Project-field names an operator marked this file ``out_of_date`` on
  // while field conflicts were resolved by hand. Empty for most files.
  out_of_date_fields: string[];
  // True when the file has been file-level marked out-of-date by
  // the operator. Distinct from ``out_of_date_fields.length > 0``
  // because a file whose extractor doesn't project onto any
  // project field (equipment_datasheet etc.) can still be marked
  // stale - the BE tracks it via a sentinel alignment row that
  // doesn't show up in the per-field list. Either flag flips the
  // file into out-of-date mode in the UI (clock icon + "Mark
  // fresh" button).
  marked_out_of_date: boolean;
  // Per-file extraction outcome. "failed" drives the Files-tab
  // "N files failed processing" badge; "unsupported" (content that
  // couldn't be extracted) is NOT a failure. "running" = a multi-segment OCR
  // pre-warm is transcribing a large scan (see extraction_progress).
  // "skipped" is the RETIRED spelling of "unsupported". The BE keeps it as a
  // read-side alias because a deploy's old code can still write it (see
  // extraction_gate._LEGACY_UNSUPPORTED_STATUS), so the FE has to know it too:
  // without it such a row falls through to "pending" and shows as ⏳ extracting
  // for ever, and the re-extract affordance - gated on failed|unsupported - is
  // never offered to the one row that needs it.
  extraction_status:
    | "pending"
    | "running"
    | "succeeded"
    | "failed"
    | "unsupported"
    | "skipped"
    | string;
  // Live OCR pre-warm progress while extraction_status === "running"; null/absent
  // otherwise. Surfaced by THIS single list poll (no per-file poll) so 100 files
  // stay one request.
  extraction_progress?: ExtractionProgress | null;
  // Set on a pending email thread the mailbox import is still adding to: when its
  // extraction will run (ISO). Null/absent otherwise.
  extraction_not_before?: string | null;
  // Row lifecycle: "active", "removed", or "quarantined". A "removed" row is a
  // tombstone (excluded gmail thread, dismissed/upstream-deleted drive file)
  // kept for the audit trail. A "quarantined" row is a gmail attachment the
  // wrong-site gate judged to belong to a DIFFERENT site (see ``quarantine``);
  // it is held back from the project canonical and shown in a review section.
  // The Files-tab failed-extraction badge scopes to active rows so an
  // excluded/quarantined file's stale status stops counting. Optional so an
  // older BE that doesn't send it degrades to "treat as active".
  status?: "active" | "removed" | "quarantined" | string;
  // Why a non-active row was removed (Drive tombstone, duplicate,
  // operator_excluded, operator_excluded_wrong_site, ...). Absent on active rows.
  removal_reason?: string | null;
  // Present on a wrong-site REMOVAL (removal_reason='operator_excluded_wrong_site'):
  // settled (not a quarantine), surfaced with a Reinstate affordance rather than a
  // review queue.
  wrong_site_exclusion?: {
    by?: string | null;
    at?: string;
    detected_other_site?: string | null;
    note?: string | null;
  } | null;
  // That removal was the wrong-site check's own confident verdict, not a person's
  // (BE ``email_relevance.is_gate_wrong_site_removal``); agents may reverse it.
  removed_by_gate?: boolean;
  // Present on a ``status === "quarantined"`` row: the wrong-site gate's verdict
  // so the review card can explain WHY the file was held back and let the
  // operator overturn it (Restore).
  quarantine?: {
    verdict?: string;
    confidence?: string;
    detected_other_site?: string | null;
    rationale?: string;
    reason?: string;
    at?: string;
    // Set once an operator has confirmed the exclusion is correct. The row stays
    // quarantined + restorable, but the review UI collapses it out of the "needs
    // a decision" list into the confirmed-exclusions section.
    confirmed?: { by?: string | null; at?: string } | null;
  } | null;
  // A kept file the wrong-site gate suspects is (also) another site's, below its
  // quarantine bar. Computed by the BE helper the agent context uses, so the badge
  // and the agent's label always agree on which files carry it.
  possible_other_site?: PossibleOtherSite | null;
  // Append-only wrong-site decision log (auto_quarantine / manual_quarantine /
  // confirm / restore), preserved across a restore so the review UI can show
  // each file's history.
  relevance_log?: Array<{
    action: string;
    by?: string | null;
    at?: string;
    note?: string;
  }> | null;
  // True when the relevance gate judged this file to cover MORE THAN ONE site (a
  // portfolio email/doc). The file still contributes, but a value read from it may
  // belong to a DIFFERENT site - so it's badged on the Files tab + on each conflict/
  // suggestion candidate to warn the operator to check the value belongs to THIS
  // site. multi_site_note is the gate's rationale (badge tooltip).
  references_multiple_sites?: boolean;
  multi_site_note?: string | null;
  // Character count of the EXTRACTED TEXT the agent reads for this file (what the
  // extract_* tools return), i.e. its true context cost - NOT the file's byte
  // size. Surfaced inline per row so an operator can see how much text was pulled
  // out, and click through to the full text. Null for files with no agent-readable
  // text (images / CAD) or not yet computed. Absent on legacy rows.
  extracted_text_chars?: number | null;
  // Page count of the source document, when it has a page concept at all - NULL
  // for xlsx / plain text, and on rows extracted before it was recorded. Preferred
  // over the char count in the row label: "12 pages" is how an operator thinks
  // about a document's size, where "48,213 chars" is a machine's view of it.
  total_pages?: number | null;
  // The persisted classified kind (e.g. "email_thread"). Prefer this
  // over inferring the type from the filename. Absent on legacy rows.
  kind?: string;
  // True when this upload is a platform-generated output (a deliverable
  // such as a generated artifact or a cross-site comparison PDF) rather
  // than an operator-supplied source document. Server-computed from the
  // canonical AGENT_OUTPUT_UPLOAD_KINDS set. Outputs still show on the
  // Files tab but are excluded from a run's input file_refs so an agent
  // never re-ingests its own output. Absent on legacy rows (treat as
  // false).
  is_agent_output?: boolean;
  // An email thread's attachment, nested under its thread on the Files tab.
  // Server-computed from the upload's origin.
  email_attachment: boolean;
  // Set when this upload was auto-pulled by the Drive folder
  // subscription. The FE filters these out of the regular files
  // list - they belong in the dedicated drive-synced tree.
  parent_drive_folder_id?: string | null;
  // Every place this unique file's bytes were seen (content identity layer 1).
  // Absent on legacy rows (treat as an empty list). One batched list poll
  // carries them all (principle 8/31) - no per-file fetch.
  sightings?: Sighting[];
  // Upload-level deprecated flag derived from the sightings (design 3.6): the
  // filed Drive copy(ies) are all at a "deprecated" path, or (no Drive copy)
  // every sighting's name flags it. Absent/false on legacy rows.
  deprecated?: boolean;
}

// A deleted file whose bytes are STILL arriving via an automated path (design
// 3.3 step 3): the operator deleted it but a Drive re-sync / Gmail re-pull keeps
// re-seeing the same bytes. Surfaced so the suppression is visible, never silent.
export interface DeletedArrival {
  upload_id: string;
  original_name: string;
  sightings: Array<{ source: string; display_name: string }>;
}

interface UploadsListResponse {
  uploads: UploadRecord[];
  // Operator-deleted files that keep arriving (see DeletedArrival). Optional so
  // an older BE / stale cache degrades to "no deleted section".
  deleted_arrivals?: DeletedArrival[];
}

const QUERY_KEY = QK.uploadsList;

const fetchUploadsList =
  (
    http: ReturnType<typeof useAgentsHttp>,
    project_id: string | null | undefined,
  ) =>
  async (): Promise<UploadsListResponse> => {
    if (!project_id) return { uploads: [] };
    return http<UploadsListResponse>("/uploads/list", { project_id });
  };

export const useUploadsList = (project_id: string | null | undefined) => {
  const http = useAgentsHttp();
  return useQuery<UploadsListResponse, Error, UploadRecord[]>(
    [QUERY_KEY, project_id ?? null],
    fetchUploadsList(http, project_id),
    {
      // Refetch when the operator returns to the tab so the Files-tab
      // failed-extraction badge + the list self-heal after an extraction that
      // finished while the tab was in the background. ``staleTime`` gates it so rapid tab
      // switches inside 60s don't re-hit the API on every focus.
      refetchOnWindowFocus: true,
      enabled: Boolean(project_id),
      staleTime: 60_000,
      // Poll while any file is extracting: fast while one is "running" so the
      // progress bar advances live, slower while one only waits on the queue
      // (a re-extract, a fresh upload). A pending email thread held for its
      // mailbox import to settle is not waiting on anything, so it does not
      // poll. Stops at the steady state to avoid a permanent background poll.
      // The callback receives the SELECTED data (UploadRecord[]) in react-query v3.
      refetchInterval: (uploads) => {
        const rows = uploads ?? [];
        if (rows.some((u) => u.extraction_status === "running")) return 3000;
        const now = Date.now();
        const anyQueued = rows.some(
          (u) =>
            u.extraction_status === "pending" &&
            !(
              u.extraction_not_before &&
              Date.parse(u.extraction_not_before) > now
            ),
        );
        return anyQueued ? 5000 : false;
      },
      select: (resp) => resp.uploads,
    },
  );
};

// Reads the deleted-arrivals section off the SAME /uploads/list query (shared
// query key -> one network call). Drives the Files-tab "deleted but still
// arriving" collapsed section (design 3.3 step 3).
export const useDeletedArrivals = (
  project_id: string | null | undefined,
): DeletedArrival[] => {
  const http = useAgentsHttp();
  const { data } = useQuery<UploadsListResponse, Error, DeletedArrival[]>(
    [QUERY_KEY, project_id ?? null],
    fetchUploadsList(http, project_id),
    {
      refetchOnWindowFocus: true,
      enabled: Boolean(project_id),
      staleTime: 60_000,
      select: (resp) => resp.deleted_arrivals ?? [],
    },
  );
  return data ?? [];
};
